/**
 * The balance, decomposed — invariant 9, as amended by register §F26l.
 *
 * **Four components, not five.** `pending_withdrawal` and `withdrawable` were
 * deleted with the custodial model: there is no withdrawal, because the money
 * never left the user's wallet. A screen rendering either is building the
 * retired model, and `check-banned-names.mjs` rejects the property read.
 *
 * **`equity` is a reconstruction over chain state, not a ledger identity**, and
 * that is strictly stronger. `available` is USDC `balanceOf`, `locked_in_positions`
 * is ERC-1155 holdings at cost basis, `pending_settlement` is
 * won-reported-unredeemed. The four sum to what Arbitrum says, which is what
 * Phase 8's third exit criterion asserts — the venue agreeing with the chain
 * rather than the venue agreeing with itself.
 *
 * **`order_locked` is the one component with no on-chain source, and it is not a
 * lock.** Orders rest off-chain, so nothing reserves that USDU; it sits in the
 * user's own wallet and they can ask to unwrap it, at which point it leaves the
 * balance and the order can no longer fill (§F76 delays the payout, not the
 * leaving). It is rendered *separately* and labelled **reserved** — separately,
 * because the components then sum to `balanceOf` and an inflated reservation is
 * a visible gap rather than something hidden inside `available`.
 *
 * This module is in `domain/` and imports no React (invariant 10): the identity
 * is arithmetic over `bigint` micro-USDC, testable without a DOM, and no float
 * ever touches it (invariant 6).
 */

import { formatFixed, parseFixed, sum, USDC_DP } from '@/lib/decimal'

/**
 * The four, in render order — and the order is the money's own story: what you
 * can spend, what you have promised, what you are holding, what is on its way
 * back. Nothing else may be added here without an entry in the register.
 */
export const BALANCE_COMPONENTS = [
  'available',
  'order_locked',
  'locked_in_positions',
  'pending_settlement',
] as const

export type BalanceComponent = (typeof BALANCE_COMPONENTS)[number]

/**
 * The label each component wears on screen.
 *
 * `order_locked` says **Reserved**, and that is a decision (§F26l), not a
 * synonym chosen for tone. Calling it *locked* asserts that something holds the
 * money, and nothing does.
 */
export const BALANCE_LABELS: Record<BalanceComponent, string> = {
  available: 'Available',
  order_locked: 'Reserved',
  locked_in_positions: 'In positions',
  pending_settlement: 'Settling',
}

export interface BalanceRow {
  key: BalanceComponent
  label: string
  /** The wire's decimal string, unrounded — what gets formatted for display. */
  value: string
  /** Exact micro-USDC, for ordering and for the identity check. */
  micro: bigint
}

export interface BalanceView {
  rows: readonly BalanceRow[]
  /** `equity` as the venue reported it, or `null` if it was absent or malformed. */
  equity: string | null
  equityMicro: bigint | null
  /** The four, added exactly. */
  componentSumMicro: bigint
  /**
   * Does the identity hold?
   *
   * `null` when there is nothing to compare against — an absent `equity` is not
   * a broken identity, it is a payload that has not arrived. `false` is a real
   * disagreement and the panel says so out loud rather than picking whichever
   * number reads better.
   */
  reconciles: boolean | null
}

/**
 * Project a `Balance` payload into what the screen renders.
 *
 * Tolerant of a missing or malformed component on purpose: this reads an
 * untyped payload, and a balance panel that throws leaves the user with no
 * number at all. A component that will not parse renders as zero **and** breaks
 * `reconciles`, which is the honest pair — the sum is visibly wrong rather than
 * quietly right.
 */
export function balanceView(payload: unknown): BalanceView {
  const balances = (payload as { balances?: Record<string, unknown> } | null)?.balances ?? {}

  const rows: BalanceRow[] = BALANCE_COMPONENTS.map((key) => {
    const raw = balances[key]
    const value = typeof raw === 'string' ? raw : '0.000000'
    return { key, label: BALANCE_LABELS[key], value, micro: toMicro(value) }
  })

  const equityRaw = balances.equity
  const equity = typeof equityRaw === 'string' ? equityRaw : null
  const equityMicro = equity === null ? null : toMicro(equity)
  const componentSumMicro = sum(rows.map((r) => r.micro))

  return {
    rows,
    equity,
    equityMicro,
    componentSumMicro,
    reconciles: equityMicro === null ? null : equityMicro === componentSumMicro,
  }
}

/**
 * **The trading balance: the one number the header shows**, exactly.
 *
 * `wallet_balance` — the wallet's USDU — and `available` until the venue has
 * observed the wallet, which is `<BalanceChip>`'s rule and the venue's for a
 * username claim (`docs/06` §F136): the client's gate and the server's read
 * one number. `null` while no balance has arrived or neither figure parses —
 * **unread, never zero**, so nothing is refused on a number that has not
 * arrived yet.
 */
export function tradingBalanceOf(payload: unknown): { value: string; micro: bigint } | null {
  const body = payload as { wallet_balance?: unknown; balances?: Record<string, unknown> } | null
  for (const raw of [body?.wallet_balance, body?.balances?.available]) {
    if (typeof raw !== 'string') continue
    try {
      return { value: raw, micro: parseFixed(raw, USDC_DP) }
    } catch {
      continue
    }
  }
  return null
}

/** The four added, as a decimal string. Only ever shown when it disagrees. */
export function formatMicro(micro: bigint): string {
  return formatFixed(micro, USDC_DP)
}

function toMicro(value: string): bigint {
  try {
    return parseFixed(value, USDC_DP)
  } catch {
    // A component that will not parse is zero *and* breaks the identity, which
    // is what surfaces it. Swallowing it into the sum would hide the defect
    // behind a balance that adds up.
    return 0n
  }
}

/**
 * The balance as the wallet sees it: three parts that add up to the trading
 * balance the header shows.
 *
 *     wallet_balance = available + order_locked + unsettled
 *
 * `balanceView`'s four add up to `equity`, which is the venue's own total and
 * not a number anybody holds — a wallet with trades waiting to settle on chain
 * showed one figure in the header and a much smaller one on the panel, and
 * nothing on the page said where the rest was. **`unsettled` is where it was**:
 * the cost of every fill whose market has not settled on chain yet, still in
 * the wallet and already out of `available`. Shares sold since are in it too,
 * because a trade's money moves only at its market's one settlement.
 *
 * `null` when the venue has not sent the wallet terms — a venue with no chain
 * reading for this account, or the frozen mock. The panel then shows
 * `balanceView`'s four, as it did before.
 */
export interface WalletView {
  /** `wallet_balance`, the header's number. */
  total: { value: string; micro: bigint }
  /** Available to trade, held for open orders, in trades waiting to settle — the sum. */
  rows: readonly { key: 'available' | 'order_locked' | 'unsettled'; label: string; value: string; micro: bigint }[]
  /** `unsettled`, split by where it waits. The two add up to it. */
  waiting: { trading: bigint; ended: bigint }
  /** A forecast, outside the sum: sale proceeds and winnings not yet delivered. */
  incoming: { value: string; micro: bigint } | null
  sumMicro: bigint
  /** Do the three add up to the wallet? */
  reconciles: boolean
  /**
   * `available` was floored at zero: a withdrawal took money out of the wallet
   * while orders still reserved it, so reserved + waiting exceed the balance.
   * The sum is then expected to overshoot, and the panel says why rather than
   * calling it a disagreement.
   */
  overReserved: boolean
}

/**
 * The three lines of the written-out sum (`= Trading balance`). Phrased as what
 * the money is doing for the user, because the panel is read as a receipt: the
 * first line is what they can spend, the next two are why the rest is not.
 */
export const WALLET_LABELS = {
  available: 'Available to trade',
  order_locked: 'Held for your open orders',
  unsettled: 'In trades waiting to settle',
} as const

export function walletView(payload: unknown): WalletView | null {
  const body = (payload as Record<string, unknown> | null) ?? {}
  const balances = (body.balances as Record<string, unknown> | undefined) ?? {}
  const str = (v: unknown) => (typeof v === 'string' ? v : null)

  const wallet = str(body.wallet_balance)
  const unsettled = str(body.unsettled)
  if (wallet === null || unsettled === null) return null

  const rows = (['available', 'order_locked'] as const).map((key) => {
    const value = str(balances[key]) ?? '0.000000'
    return { key, label: WALLET_LABELS[key], value, micro: toMicro(value) }
  })
  const waitingMicro = toMicro(unsettled)
  const all = [...rows, { key: 'unsettled' as const, label: WALLET_LABELS.unsettled, value: unsettled, micro: waitingMicro }]

  let ended = toMicro(str(body.unsettled_ended) ?? '0.000000')
  if (ended > waitingMicro) ended = waitingMicro
  const incomingRaw = str(body.incoming)

  const totalMicro = toMicro(wallet)
  const sumMicro = sum(all.map((r) => r.micro))
  return {
    total: { value: wallet, micro: totalMicro },
    rows: all,
    waiting: { trading: waitingMicro - ended, ended },
    incoming: incomingRaw === null ? null : { value: incomingRaw, micro: toMicro(incomingRaw) },
    sumMicro,
    reconciles: sumMicro === totalMicro,
    overReserved: (rows[0]?.micro ?? 0n) === 0n && sumMicro > totalMicro,
  }
}
